// Links an agency types into its profile end up in href attributes on its public
// page. Only http and https are allowed: a javascript: or data: URL would run in
// the browser of every visitor who clicks it.

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

export function safeExternalUrl(value: string | null | undefined): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  // A bare domain ("agency.ca") is a link, not a scheme-relative URL.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//') ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null;
    if (!url.hostname || !url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

// WhatsApp and Telegram are stored as a number or a handle, not a URL.
export function whatsAppLink(value: string | null | undefined): string | null {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;
  return `https://wa.me/${digits}`;
}

export function telegramLink(value: string | null | undefined): string | null {
  const handle = String(value || '').trim().replace(/^@/, '').replace(/^https?:\/\/(t\.me|telegram\.me)\//i, '');
  if (!/^[a-zA-Z0-9_]{4,32}$/.test(handle)) return null;
  return `https://t.me/${handle}`;
}

// Redirect targets that came from a query parameter: same-origin paths only, so
// ?from=//evil.com cannot bounce a visitor off the site.
//
// Item 67. The previous version inspected the start of the string by hand -
// must begin with "/", must not begin with "//" or "/\\" - and then returned
// the string it had inspected. The browser does not parse the string that was
// inspected. It strips tab, newline and carriage return from anywhere in a URL
// before parsing, so:
//
//   "/\u0009//evil.com"  passes every check above, and resolves to https://evil.com/
//
// It starts with a single slash. It does not start with "//" or "/\\". The
// CR/LF guard does not cover tab. Confirmed by running it.
//
// The sink is reachable: the construction gate sends a visitor to
// /maintenance?from=<path> and redirects them to `from` once they are let in.
// A link on this site's own domain therefore landed them on somebody else's,
// which is what a phishing page wants more than anything.
//
// So this no longer reasons about the text. It hands the value to the same
// parser the browser uses, against an origin that exists nowhere, and requires
// the result to have stayed on it. What is returned is what the parser
// produced, not what arrived - a path that survives this cannot mean one thing
// here and another in a browser.
const REDIRECT_PROBE_ORIGIN = 'https://internal.invalid';

export function safeInternalPath(value: string | null | undefined, fallback: string): string {
  const raw = String(value || '');

  // Control characters and whitespace are rejected outright rather than
  // trimmed: every one of them is a way to make the parser and a hand-written
  // check disagree, and none of them belongs in a path this site produced.
  if (!raw || /[\u0000-\u0020\u007f]/.test(raw)) return fallback;
  if (!raw.startsWith('/')) return fallback;

  let parsed: URL;
  try {
    parsed = new URL(raw, REDIRECT_PROBE_ORIGIN);
  } catch {
    return fallback;
  }

  // Anything that reached another origin - through "//host", a backslash, a
  // scheme, or a character the parser dropped - is not an internal path.
  if (parsed.origin !== REDIRECT_PROBE_ORIGIN) return fallback;

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return path.startsWith('//') ? fallback : path;
}
